import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import dayjs from "dayjs";
import {
    apiGetJson,
    apiGet,
    apiPutJson,
    apiDeleteJson,
    apiPostFormData,
    apiPostJson,
} from "../utils/apiClient";
import { makeItemKey } from "../utils/makeItemKey";
import { EditableRow } from "../types/EditableRow";
import { MonthlyItemCostTable } from "../components/MonthlyItemCostTable";
import LoadingInline from "../components/LoadingInline";
import { ToastViewport, useToast } from "../components/Toast";
import { CostSupportInfo } from "../components/CostSupportInfo";
import { SupportAnchorBanner } from "../components/SupportAnchorBanner";
import NextEngineConnectModal from "../components/NextEngineConnectModal";
import NeSyncStatusBanner from "../components/NeSyncStatusBanner";
import { useNeJobPolling } from "../hooks/useNeJobPolling";
import { useUserContext } from "../contexts/UserContext";
import { isFreePlanUser } from "../utils/plan";

/** ★ apiClient と同じ判定（exportされてないのでページ側に最小実装） */
function isDemoModeLocal(): boolean {
    if (typeof window === "undefined") return false;
    if (window.location.pathname.startsWith("/demo")) return true;
    const ls = localStorage.getItem("demoMode");
    return ls === "1" || ls === "true";
}

type MonthDef = { ym: string; label: string };

type CoverageSummary = {
    year_month: string;
    total_items: number;
    registered_items: number;
    unregistered_items: number;
    registration_rate: number;
    unregistered_details: {
        item_code: string;
        sku_code: string;
        item_key: string;
    }[];
};

const pad2 = (n: number) => String(n).padStart(2, "0");
function clamp(n: number, min: number, max: number) {
    return Math.min(Math.max(n, min), max);
}
function normalizeStartMonth(v: any): number {
    const n = Number(v);
    if (!Number.isFinite(n)) return 1;
    return clamp(Math.trunc(n), 1, 12);
}

/** ✅ “今” と 期首月から、デフォルトで表示すべき年度(=開始年) を決める */
function computeDefaultYearForStartMonth(now: dayjs.Dayjs, startMonth: number) {
    const sm = normalizeStartMonth(startMonth);
    const startThisYear = dayjs(`${now.year()}-${pad2(sm)}-01`);
    return now.isBefore(startThisYear) ? now.year() - 1 : now.year();
}

/** ✅ 年度(開始年) + 期首月 から 12ヶ月を作る */
function buildMonthColsFromYearAndStartMonth(year: number, startMonth: number): MonthDef[] {
    const sm = normalizeStartMonth(startMonth);
    const start = dayjs(`${year}-${pad2(sm)}-01`).startOf("month");
    return Array.from({ length: 12 }, (_, i) => {
        const d = start.add(i, "month");
        return { ym: d.format("YYYY-MM"), label: d.format("YYYY/MM") };
    });
}

function yearRangeLabelFromYearStartMonth(year: number, startMonth: number): string {
    const cols = buildMonthColsFromYearAndStartMonth(year, startMonth);
    const s = cols[0]?.label ?? "";
    const e = cols[cols.length - 1]?.label ?? "";
    return s && e ? `${s}〜${e}` : "";
}

function emptyMonths12(): (number | null)[] {
    return Array(12).fill(null);
}

function rate01ToView(v: number | null): string {
    if (v == null) return "";
    return (Math.round(v * 1000) / 10).toString();
}
function viewToRate01(s: string): number | null {
    const t = String(s ?? "").trim();
    if (t === "") return null;
    const n = Number(t);
    if (!Number.isFinite(n)) return null;
    return n / 100;
}
function normalizeRates12(arr?: any, fallbackSingle?: number | null): (number | null)[] {
    if (Array.isArray(arr) && arr.length === 12) return arr as (number | null)[];
    if (fallbackSingle != null) return Array(12).fill(fallbackSingle);
    return emptyMonths12();
}

function makeClientRowId(prefix = "row"): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function makeNewEmptyRow(): EditableRow {
    return {
        client_row_id: makeClientRowId("new"),
        item_key: "__new__",
        old_item_key: "",
        item_code: "",
        sku_code: "",
        cost: null,
        monthly_costs: emptyMonths12(),
        isNew: true,
    } as any;
}

function hasAnyInputForRow(row: any): boolean {
    const hasKey =
        Boolean((row?.item_code ?? "").trim()) ||
        Boolean((row?.sku_code ?? "").trim());

    const arr = row?.monthly_costs;
    const hasAnyCost = Array.isArray(arr) && arr.some((v: any) => v !== null && v !== undefined && v !== "");

    const hasLegacyCost = row?.cost !== null && row?.cost !== undefined && row?.cost !== "";

    return hasKey || hasAnyCost || hasLegacyCost;
}

function ensureNewRowIfNeeded(list: EditableRow[], isFreePlan: boolean): EditableRow[] {
    if (isFreePlan) return list;
    const hasNew = list.some((r) => r.item_key === "__new__");
    return hasNew ? list : [...list, makeNewEmptyRow()];
}

/** ✅ 期首月を取得する設定API（FiscalRakutenExpensePage と同じ） */
const SETTINGS_ENDPOINT = "/api/company/settings/fiscal";
async function fetchFiscalStartMonth(shopUrl: string): Promise<number | null> {
    const d = await apiGetJson<any>(`${SETTINGS_ENDPOINT}?shop_url=${encodeURIComponent(shopUrl)}`);
    const m = d?.fiscalMonth ?? null;
    return m == null ? null : Number(m);
}

type ParsedItemCostsApiError = {
    status: number | null;
    code: string | null;
    message: string | null;
    detail: string | null;
    retryAfterSec: number | null;
    raw: string;
};

function parseItemCostsApiError(err: any): ParsedItemCostsApiError {
    const raw = String(err?.message ?? err ?? "");
    const m = raw.match(/^(\d{3})\s+[^:]+:\s*([\s\S]*)$/);
    const status = m ? Number(m[1]) : null;
    const bodyText = m ? m[2] : raw;

    try {
        const body = JSON.parse(bodyText);
        return {
            status,
            code: typeof body?.error === "string" ? body.error : null,
            message: typeof body?.message === "string" ? body.message : null,
            detail: typeof body?.detail === "string" ? body.detail : null,
            retryAfterSec: Number.isFinite(Number(body?.retry_after_sec)) ? Number(body.retry_after_sec) : null,
            raw,
        };
    } catch {
        const lower = bodyText.toLowerCase();
        const inferredCode =
            lower.includes("write_locked")
                ? "write_locked"
                : lower.includes("concurrent update")
                    ? "concurrent_update"
                    : null;

        return {
            status,
            code: inferredCode,
            message: bodyText || null,
            detail: null,
            retryAfterSec: null,
            raw,
        };
    }
}

function formatItemCostsWriteError(actionLabel: string, err: any): string {
    const parsed = parseItemCostsApiError(err);
    const serverMessage = parsed.message || parsed.detail || parsed.raw;

    if (parsed.code === "write_locked" || parsed.code === "concurrent_update") {
        const retryLine = parsed.retryAfterSec ? `\n${parsed.retryAfterSec}秒ほど待って再試行してください。` : "";
        return `${actionLabel}に失敗しました。\n${serverMessage}${retryLine}`;
    }

    return `${actionLabel}に失敗: ${serverMessage}`;
}

function formatItemCostsStatusError(actionLabel: string, rawMessage: string): string {
    const lower = String(rawMessage || "").toLowerCase();
    if (lower.includes("concurrent update") || lower.includes("could not serialize access") || lower.includes("aborted")) {
        return `${actionLabel}に失敗しました。\n同時更新が発生しました。少し待って再試行してください。`;
    }
    return `${actionLabel}が失敗しました\n${rawMessage || "error"}`;
}

export default function MonthlyItemCostPage({ shopUrl }: { shopUrl: string }) {
    const user = useUserContext();
    const isFreePlan = useMemo(() => isFreePlanUser(user), [user]);
    const isSuper = !!(
        (user as any)?.superAdmin ||
        (user as any)?.superAdminGlobal
    );

    // ネクストエンジン連携モーダルの開閉
    const [neModalOpen, setNeModalOpen] = useState(false);
    // 原価取得ジョブのポーリングはページ側が持つ(取得を始めたらモーダルは閉じ、
    // 経過と結果は原価一覧の画面=バナーで見せる)。
    const nePolling = useNeJobPolling();
    const neJob = nePolling.job;
    const neJobRunning = nePolling.polling && !(neJob && ["succeeded", "partial", "failed", "cancelled"].includes(neJob.status));

    const isDemo = useMemo(() => isDemoModeLocal(), []);

    // ✅ 成功・失敗のお知らせ（共通トースト）
    const { toasts, pushToast, removeToast } = useToast();

    // ✅ shopUrl が空で encodeURIComponent に入ると落ちる/変なAPIになるのでガード（FiscalRakutenExpensePage と同じ）
    const shopUrlSafe = useMemo(() => {
        const trimmed = String(shopUrl ?? "").trim();
        if (trimmed) return trimmed;
        return isDemo ? "demo-shop" : "";
    }, [shopUrl, isDemo]);

    const missingShopUrl = !isDemo && !shopUrlSafe;

    // =========================================================
    // ✅ 設定ロード（期首月）
    // =========================================================
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [startMonth, setStartMonth] = useState<number>(1);

    /** ✅ 年度（＝開始年） */
    const userChangedYearRef = useRef(false);
    const [year, setYear] = useState<number>(() => computeDefaultYearForStartMonth(dayjs(), 1));

    /**
     * ✅ shop 切替・レース対策
     * - token が一致する時だけ state 反映
     */
    const loadTokenRef = useRef(0);

    // ✅ 追加：shop 切替のたびに状態をリセット＋「年度自動」を許可
    useEffect(() => {
        loadTokenRef.current++;
        userChangedYearRef.current = false;

        // 月次画面固有の state リセット（ズレ防止）
        setRows([]);
        setTotal(0);
        setPage(1);
        setKeyword("");

        setSelectedKeys(new Set());

        setDirty(false);
        setDirtyHintDismissed(false);

        setCoverageYearMonth(dayjs().format("YYYY-MM")); // ← 追加
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopUrlSafe]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            setSettingsLoading(true);

            try {
                if (isDemo) {
                    const sm = 1;
                    if (cancelled) return;
                    setStartMonth(sm);
                    if (!userChangedYearRef.current) {
                        setYear(computeDefaultYearForStartMonth(dayjs(), sm));
                    }
                    return;
                }

                if (!shopUrlSafe) {
                    // shopUrl 未指定は「設定取得不可」なので 1月期のまま
                    if (!cancelled) {
                        setStartMonth(1);
                        if (!userChangedYearRef.current) {
                            setYear(computeDefaultYearForStartMonth(dayjs(), 1));
                        }
                    }
                    return;
                }

                const raw = await fetchFiscalStartMonth(shopUrlSafe);
                const sm = normalizeStartMonth(raw ?? 1);

                if (cancelled) return;

                setStartMonth(sm);
                if (!userChangedYearRef.current) {
                    setYear(computeDefaultYearForStartMonth(dayjs(), sm));
                }
            } catch {
                if (!cancelled) {
                    setStartMonth(1);
                    if (!userChangedYearRef.current) {
                        setYear(computeDefaultYearForStartMonth(dayjs(), 1));
                    }
                }
            } finally {
                if (!cancelled) setSettingsLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [shopUrlSafe, isDemo]);

    // =========================================================
    // ✅ 12ヶ月（年度+期首月）で作る
    // =========================================================
    const monthColsSafe = useMemo(() => {
        return buildMonthColsFromYearAndStartMonth(year, startMonth);
    }, [year, startMonth]);

    /** ✅ この画面の “year_month” は「期首（YYYY-MM）」を使う */
    const yearMonth = useMemo(() => monthColsSafe[0]?.ym || dayjs().format("YYYY-MM"), [monthColsSafe]);
    const ymDate = `${yearMonth}-01`;

    const [coverageYearMonth, setCoverageYearMonth] = useState<string>(() => dayjs().format("YYYY-MM"));

    // =========================================================
    // state
    // =========================================================
    const [rows, setRows] = useState<EditableRow[]>([]);

    const [, setAvgCostRate] = useState<number | null>(null);
    const [, setAvgCostRateView] = useState<string>("");

    const [, setAvgCostRates] = useState<(number | null)[]>(emptyMonths12());
    const [avgCostRatesView, setAvgCostRatesView] = useState<string[]>(Array(12).fill(""));

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [getting, setGetting] = useState(false);
    const [importing, setImporting] = useState(false);

    const [page, setPage] = useState(1);
    const pageSize = 200;
    const [total, setTotal] = useState(0);

    const [keyword, setKeyword] = useState("");

    const [coverageSummary, setCoverageSummary] = useState<CoverageSummary>({
        year_month: `${coverageYearMonth}-01`,
        total_items: 0,
        registered_items: 0,
        unregistered_items: 0,
        registration_rate: 0,
        unregistered_details: [],
    });

    const [coverageLoading, setCoverageLoading] = useState(false);

    // ✅ 未保存トースト
    const [dirty, setDirty] = useState(false);
    const [dirtyHintDismissed, setDirtyHintDismissed] = useState(false);
    useEffect(() => {
        if (dirty) setDirtyHintDismissed(false);
    }, [dirty]);

    // ✅ 未保存ガード：タブ閉じ/リロード時にブラウザ標準の確認を出す
    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    // ✅ 未保存ガード：年度切替・ページ切替の前に確認
    const confirmDiscardIfDirty = useCallback(() => {
        if (!dirty) return true;
        return window.confirm("未保存の変更があります。\n移動すると入力内容は破棄されます。よろしいですか？");
    }, [dirty]);

    const handleAvgRateChange = useCallback((idx: number, value: string) => {
        setDirty(true);
        setAvgCostRatesView((prev) => {
            const next = Array.isArray(prev) && prev.length === 12 ? [...prev] : Array(12).fill("");
            next[idx] = value;
            if (idx === 0) {
                setAvgCostRateView(value);
                setAvgCostRate(viewToRate01(value));
            }
            return next;
        });
    }, []);

    // =========================================================
    // ✅ 一括削除（チェック選択）
    // =========================================================
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [allDeleting, setAllDeleting] = useState(false);

    // この画面で「削除対象として選べる」行（新規行は除外）
    const selectableKeys = useMemo(() => {
        const keys = (rows ?? [])
            .filter((r: any) => !r?.isNew && r?.item_key !== "__new__")
            .map((r: any) => String(r.old_item_key || r.item_key))
            .filter((k: string) => k && k !== "__new__");
        return keys;
    }, [rows]);

    const selectedCount = useMemo(() => selectedKeys.size, [selectedKeys]);
    const selectableCount = useMemo(() => selectableKeys.length, [selectableKeys]);

    const onToggleSelect = useCallback((key: string, checked: boolean) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (checked) next.add(key);
            else next.delete(key);
            return next;
        });
    }, []);

    const onToggleSelectAll = useCallback((checked: boolean) => {
        setSelectedKeys(() => {
            if (!checked) return new Set();
            return new Set(selectableKeys);
        });
    }, [selectableKeys]);

    // load は下で定義（依存に入れるため ref で持つ）
    const loadRef = useRef<null | (() => Promise<void>)>(null);

    const loadCoverageSummary = useCallback(async () => {
        if (settingsLoading) return;
        if (missingShopUrl) return;

        setCoverageLoading(true);
        try {
            const data = await apiGetJson<CoverageSummary>(
                `/api/item-costs/coverage-summary?shop_url=${encodeURIComponent(shopUrlSafe)}&year_month=${encodeURIComponent(coverageYearMonth)}`
            );

            setCoverageSummary({
                year_month: String(data?.year_month ?? `${coverageYearMonth}-01`),
                total_items: Number(data?.total_items ?? 0),
                registered_items: Number(data?.registered_items ?? 0),
                unregistered_items: Number(data?.unregistered_items ?? 0),
                registration_rate: Number(data?.registration_rate ?? 0),
                unregistered_details: Array.isArray(data?.unregistered_details)
                    ? data.unregistered_details.map((x) => ({
                        item_code: String(x?.item_code ?? ""),
                        sku_code: String(x?.sku_code ?? ""),
                        item_key: String(x?.item_key ?? ""),
                    }))
                    : [],
            });
        } catch (e) {
            // RMS/CSV認証情報が未設定などで取得できない場合でも画面を壊さない。
            // 登録状況は空表示にとどめ、未処理エラー（Uncaught）を防ぐ。
            console.warn("[MonthlyItemCost] coverage-summary の取得に失敗しました", e);
            setCoverageSummary({
                year_month: `${coverageYearMonth}-01`,
                total_items: 0,
                registered_items: 0,
                unregistered_items: 0,
                registration_rate: 0,
                unregistered_details: [],
            });
        } finally {
            setCoverageLoading(false);
        }
    }, [settingsLoading, missingShopUrl, shopUrlSafe, coverageYearMonth]);

    const handleBulkDeleteSelected = useCallback(async () => {
        if (isFreePlan) return;
        if (bulkDeleting || allDeleting) return;
        if (missingShopUrl) return;

        const keys = Array.from(selectedKeys);
        if (keys.length === 0) {
            pushToast("error", "削除する行が選択されていません", 5000);
            return;
        }

        if (!window.confirm(`選択した ${keys.length} 件を削除しますか？\n（この操作は取り消せません）`)) return;

        setBulkDeleting(true);
        try {
            const resp = await apiPostJson("/api/item-costs/bulk-delete", {
                shop_url: shopUrlSafe,
                year_month: ymDate,
                item_keys: keys,
            });

            pushToast("success", `一括削除しました（${resp?.requested ?? keys.length}件）`);

            setSelectedKeys(new Set());
            await (loadRef.current?.() ?? Promise.resolve());
            await loadCoverageSummary();
        } catch (e: any) {
            pushToast("error", formatItemCostsWriteError("一括削除", e), 0);
        } finally {
            setBulkDeleting(false);
        }
    }, [isFreePlan, bulkDeleting, allDeleting, selectedKeys, shopUrlSafe, ymDate, missingShopUrl, loadCoverageSummary, pushToast]);

    const handleDeleteAll = useCallback(async () => {
        if (isFreePlan) return;
        if (allDeleting || bulkDeleting) return;
        if (missingShopUrl) return;

        const confirmed = window.prompt(
            "この店舗に紐づく商品原価データを全件削除します。\n\n" +
            "対象:\n" +
            "- 商品原価（全月）\n\n" +
            "※平均商品原価率は削除されません。\n" +
            "この操作は取り消せません。\n" +
            "実行する場合は delete と入力してください。"
        );

        if (confirmed !== "delete") {
            pushToast("info", "全件削除を中止しました", 5000);
            return;
        }

        setAllDeleting(true);
        try {
            const resp = await apiPostJson("/api/item-costs/delete-all", {
                shop_url: shopUrlSafe,
            });

            pushToast(
                "success",
                `全件削除しました\n` +
                `商品原価: ${resp?.deletedItemRows ?? 0}件`,
                6000
            );

            setSelectedKeys(new Set());
            setRows(ensureNewRowIfNeeded([], isFreePlan));
            setTotal(0);
            setDirty(false);
            setDirtyHintDismissed(false);

            if (page !== 1) {
                setPage(1);
            } else {
                await (loadRef.current?.() ?? Promise.resolve());
                await loadCoverageSummary();
            }
        } catch (e: any) {
            pushToast("error", formatItemCostsWriteError("全件削除", e), 0);
        } finally {
            setAllDeleting(false);
        }
    }, [isFreePlan, allDeleting, bulkDeleting, missingShopUrl, shopUrlSafe, page, loadCoverageSummary, pushToast]);

    // =========================================================
    // ★一括コピー（既存）
    // =========================================================
    type BulkScope = "single" | "range12";
    type BulkMode = "insert_only" | "fill" | "overwrite";

    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkScope, setBulkScope] = useState<BulkScope>("single");
    const [bulkMode, setBulkMode] = useState<BulkMode>("insert_only");

    // ✅ 初期値：現状どおり（単月コピーで「前月→今年度期首」だと違和感出るので、開いたときに調整）
    const [bulkSrcYm, setBulkSrcYm] = useState(dayjs().subtract(1, "month").format("YYYY-MM"));
    const [bulkDstYm, setBulkDstYm] = useState(dayjs().format("YYYY-MM"));

    const [bulkIncludeRates, setBulkIncludeRates] = useState(true);

    const [bulkRunId, setBulkRunId] = useState<string | null>(null);
    const [bulkStatus, setBulkStatus] = useState<any>(null);
    const [bulkRunning, setBulkRunning] = useState(false);

    const isRange12 = bulkScope === "range12";

    const bulkSrcLabel = dayjs(bulkSrcYm).format("YYYY/MM");
    const bulkDstStartLabel = dayjs(bulkDstYm).format("YYYY/MM");
    const bulkDstRangeLabel = isRange12
        ? `${bulkDstStartLabel}〜${dayjs(bulkDstYm).add(11, "month").format("YYYY/MM")}`
        : bulkDstStartLabel;

    const bulkSummaryLabel = isRange12
        ? `テンプレ: ${bulkSrcLabel} → コピー先: ${bulkDstRangeLabel}`
        : `コピー元: ${bulkSrcLabel} → コピー先: ${bulkDstStartLabel}`;

    // ✅ 期首ページなので、range12 を開いたら “今表示している期(期首)” を使う
    const prevScopeRef = useRef<BulkScope>("single");
    useEffect(() => {
        if (!bulkOpen) return;
        if (prevScopeRef.current === bulkScope) return;

        if (bulkScope === "range12") {
            setBulkSrcYm(yearMonth);
            setBulkDstYm(yearMonth);
        }
        prevScopeRef.current = bulkScope;
    }, [bulkScope, bulkOpen, yearMonth]);

    async function downloadUnregisteredCsv() {
        if (missingShopUrl) return;

        const url =
            `/api/item-costs/unregistered-csv` +
            `?shop_url=${encodeURIComponent(shopUrlSafe)}` +
            `&year_month=${encodeURIComponent(coverageYearMonth)}` +
            `&fiscal_year_month=${encodeURIComponent(yearMonth)}`;

        const res = await apiGet(url);
        if (!res.ok) {
            const txt = await res.text().catch(() => "");
            pushToast("error", `ダウンロードに失敗しました（${res.status}）\n${txt}`, 0);
            return;
        }
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = `unregistered_items.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(a.href);
    }

    async function downloadTemplateCsv() {
        if (missingShopUrl) return;

        const url =
            `/api/item-costs/template-csv?enc=utf8bom` +
            `&year_month=${encodeURIComponent(yearMonth)}` +
            `&shop_url=${encodeURIComponent(shopUrlSafe)}`;

        const res = await apiGet(url);
        if (!res.ok) {
            const txt = await res.text().catch(() => "");
            pushToast("error", `ダウンロードに失敗しました（${res.status}）\n${txt}`, 0);
            return;
        }
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = "item_cost_template.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(a.href);
    }

    async function downloadAllCsv() {
        if (missingShopUrl) return;

        const url =
            `/api/item-costs/export-all?enc=utf8bom` +
            `&shop_url=${encodeURIComponent(shopUrlSafe)}`;

        const res = await apiGet(url);
        if (!res.ok) {
            const txt = await res.text().catch(() => "");
            pushToast("error", `ダウンロードに失敗しました（${res.status}）\n${txt}`, 0);
            return;
        }
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = `item_cost_all_${shopUrlSafe}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(a.href);
    }

    async function downloadSampleXlsx() {
        const res = await fetch("/item-cost-sample.xlsx");
        if (!res.ok) {
            const txt = await res.text().catch(() => "");
            pushToast("error", `ダウンロードに失敗しました（${res.status}）\n${txt}`, 0);
            return;
        }
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = "原価登録サンプルデータ.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(a.href);
    }

    // -------------------------------------------
    // load (/range)
    // -------------------------------------------
    const load = useCallback(async () => {
        if (settingsLoading) return;
        if (missingShopUrl) return;

        const token = ++loadTokenRef.current;
        const currentShopUrl = shopUrlSafe;
        const currentYearMonth = yearMonth;
        const currentPage = page;
        const currentPageSize = pageSize;
        const currentKeyword = keyword;

        setLoading(true);
        try {
            const url =
                `/api/item-costs/range?shop_url=${encodeURIComponent(currentShopUrl)}&year_month=${encodeURIComponent(currentYearMonth)}` +
                `&page=${currentPage}&pageSize=${currentPageSize}&keyword=${encodeURIComponent(currentKeyword)}`;

            const data = await apiGetJson<{
                avg_cost_rate: number | null;
                avg_cost_rates?: (number | null)[];
                months?: string[];
                items: any[];
                total: number;
            }>(url);

            if (token !== loadTokenRef.current) return;

            setTotal(Number(data.total ?? 0));

            const months =
                Array.isArray(data.months) && data.months.length === 12
                    ? data.months
                    : buildMonthColsFromYearAndStartMonth(year, startMonth).map((m) => m.ym);

            const monthDefs = months.map((ym) => ({
                ym,
                label: dayjs(`${ym}-01`).format("YYYY/MM"),
            }));

            // ✅ 平均原価率
            const rates01 = normalizeRates12(data.avg_cost_rates, data.avg_cost_rate);
            setAvgCostRates(rates01);

            const views = rates01.map(rate01ToView);
            setAvgCostRatesView(views);

            setAvgCostRate(rates01[0] ?? null);
            setAvgCostRateView(views[0] ?? "");

            const list: EditableRow[] = (data.items ?? []).map((r) => ({
                client_row_id: makeClientRowId("loaded"),
                item_key: r.item_key,
                old_item_key: r.item_key,
                item_code: r.item_code ?? "",
                sku_code: r.sku_code ?? "",
                cost: null,
                monthly_costs: Array.isArray(r.monthly_costs) ? r.monthly_costs : emptyMonths12(),
                isNew: false,
                item_name: r.item_name ?? "",
                item_name_code: String(r.item_code ?? "").trim().toUpperCase(),
            }));

            setRows(ensureNewRowIfNeeded(list, isFreePlan));

            // ✅ ロード後は選択をクリア（ズレ防止）
            setSelectedKeys(new Set());

            // ✅ 期首由来の monthColsSafe と、サーバ返却 months がズレたとき用に、テーブルへ渡す months を monthDefs にする
            setTableMonthCols(monthDefs);

            setDirty(false);
            setDirtyHintDismissed(false);
        } catch {
            if (token !== loadTokenRef.current) return;
            setRows(ensureNewRowIfNeeded([], isFreePlan));
        } finally {
            if (token !== loadTokenRef.current) return;
            setLoading(false);
        }
    }, [settingsLoading, missingShopUrl, shopUrlSafe, yearMonth, page, pageSize, keyword, isFreePlan, year, startMonth]);

    // ✅ loadRef を常に最新に
    useEffect(() => {
        loadRef.current = load;
    }, [load]);

    // ✅ テーブルに渡す months（サーバ返却に追随させたいので state化）
    const [tableMonthCols, setTableMonthCols] = useState<MonthDef[]>(() =>
        buildMonthColsFromYearAndStartMonth(computeDefaultYearForStartMonth(dayjs(), 1), 1)
    );

    // ✅ 初回 & 条件変化でロード
    useEffect(() => {
        if (settingsLoading) return;
        if (missingShopUrl) return;

        // 期首が変わった/年度が変わったらテーブル見出しも先に合わせる
        setTableMonthCols(buildMonthColsFromYearAndStartMonth(year, startMonth));

        load();
        loadCoverageSummary();
    }, [load, loadCoverageSummary, settingsLoading, missingShopUrl, year, startMonth]);

    // NE 原価取得ジョブが完了(成功/一部成功)したら、一覧と登録状況を取り直して
    // 反映結果を画面に反映する。同じジョブで二重に走らないよう jobId で間引く。
    const neReloadedJobRef = useRef<string | null>(null);
    useEffect(() => {
        if (!neJob) return;
        if (neJob.status !== "succeeded" && neJob.status !== "partial") return;
        if (neReloadedJobRef.current === neJob.jobId) return;
        neReloadedJobRef.current = neJob.jobId;
        if (missingShopUrl) return;
        load();
        loadCoverageSummary();
    }, [neJob, load, loadCoverageSummary, missingShopUrl]);

    const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(e.target.value);
        setPage(1);
    };

    // -------------------------------------------
    // CSV import
    // -------------------------------------------
    /** ✅ 取り込み先の期首年月（YYYY-MM）を「YYYY年M月」で表示する */
    const yearMonthLabel = useMemo(
        () => dayjs(`${yearMonth}-01`).format("YYYY年M月"),
        [yearMonth]
    );

    const handleClickImport = () => {
        if (isFreePlan) return;
        if (missingShopUrl) return;
        if (
            !window.confirm(
                `${yearMonthLabel}（期首）の原価としてCSVをインポートします。よろしいですか？`
            )
        )
            return;
        if (importing || allDeleting) return;
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isFreePlan) {
            e.target.value = "";
            return;
        }
        if (missingShopUrl) {
            e.target.value = "";
            return;
        }

        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        try {
            setImporting(true);

            const fd = new FormData();
            fd.append("shop_url", shopUrlSafe);
            fd.append("year_month", yearMonth); // ✅ 期首YYYY-MM
            fd.append("file", file, file.name);

            const res = await apiPostFormData("/api/item-costs/import", fd);
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(`${res.status} /api/item-costs/import: ${JSON.stringify(json)}`);
            }

            pushToast(
                "success",
                `CSVインポート完了（${yearMonthLabel} 期首）\n` +
                `更新: ${json.candidates ?? "?"} / 追加: ${json.inserted ?? "?"} / 無視: ${json.ignored ?? "?"}`,
                6000
            );

            await load();
            await loadCoverageSummary();
        } catch (err: any) {
            pushToast("error", formatItemCostsWriteError("CSVインポート", err), 0);
        } finally {
            setImporting(false);
        }
    };

    // -------------------------------------------
    // RMS
    // -------------------------------------------
    const handleClickGetRms = async () => {
        if (isFreePlan) return;
        if (missingShopUrl) return;
        if (!window.confirm("RMSからデータを取得しますか？")) return;
        if (importing || allDeleting) return;

        try {
            setGetting(true);

            await apiGetJson(
                `/api/rms/fetch-item-skus?shop_url=${encodeURIComponent(shopUrlSafe)}&year_month=${encodeURIComponent(yearMonth)}`
            );

            pushToast("success", "RMS取り込みを開始します。時間がたってから登録を確認してください。", 6000);
            await load();
            await loadCoverageSummary();
        } catch (err: any) {
            pushToast("error", `RMS取り込み失敗: ${err.message}`, 0);
        } finally {
            setGetting(false);
        }
    };

    // -------------------------------------------
    // 入力変更
    // -------------------------------------------
    const handleChange = (id: string, field: keyof EditableRow, value: any) => {
        if (isFreePlan) return;
        setDirty(true);

        setRows((prev) => {
            const updated = prev.map((r: any) => ((r.client_row_id || r.item_key) === id ? { ...r, [field]: value } : r));
            const newRow: any = updated.find((r: any) => r.item_key === "__new__");

            if (newRow && hasAnyInputForRow(newRow)) {
                // item_key は item_code/sku_code が変わるたびに衝突するので、
                // 内部管理キーは常にユニークな __tmp__ にする（保存時は makeItemKey で再計算）
                const converted = updated.map((r: any) =>
                    r.item_key === "__new__"
                        ? {
                            ...r,
                            item_key: makeClientRowId("tmp"),
                            old_item_key: "",
                            isNew: false,
                            cost: null,
                            monthly_costs: Array.isArray(r.monthly_costs) ? r.monthly_costs : emptyMonths12(),
                        }
                        : r
                );

                converted.push(makeNewEmptyRow());
                return converted;
            }

            return ensureNewRowIfNeeded(updated, false);
        });
    };

    // -------------------------------------------
    // 保存
    // -------------------------------------------
    const handleSave = async () => {
        if (missingShopUrl) return;

        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);

        try {
            const ratesForDb = (Array.isArray(avgCostRatesView) && avgCostRatesView.length === 12 ? avgCostRatesView : Array(12).fill("")).map(viewToRate01);
            const rateForDb = ratesForDb[0] ?? null;

            let payload: any[] = [];
            if (!isFreePlan) {
                const dirtyRows = rows.filter((r: any) => {
                    if (r.isNew) return false;
                    const newKey = makeItemKey(r);
                    if (newKey === "" && r.old_item_key === "") return false;
                    return true;
                });

                payload = dirtyRows.map((r: any) => {
                    const newKey = makeItemKey(r) || r.old_item_key;
                    const monthly = Array.isArray(r.monthly_costs) ? r.monthly_costs : emptyMonths12();
                    const fixed =
                        monthly.length === 12 ? monthly : [...monthly.slice(0, 12), ...Array(Math.max(0, 12 - monthly.length)).fill(null)];

                    return {
                        old_item_key: r.old_item_key,
                        item_key: newKey,
                        item_code: r.item_code,
                        sku_code: r.sku_code,
                        monthly_costs: fixed,
                    };
                });
            }

            await apiPutJson("/api/item-costs", {
                shop_url: shopUrlSafe,
                year_month: ymDate, // ✅ 期首YYYY-MM-01
                months: (tableMonthCols.length === 12 ? tableMonthCols : monthColsSafe).map((m) => m.ym),
                avg_cost_rates: ratesForDb,
                avg_cost_rate: rateForDb,
                items: payload,
            });

            pushToast("success", "保存しました");

            setDirty(false);
            setDirtyHintDismissed(false);

            await load();
            await loadCoverageSummary();
        } catch (err: any) {
            pushToast("error", formatItemCostsWriteError("保存", err), 0);
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    // -------------------------------------------
    // 単体削除（既存）
    // -------------------------------------------
    const handleDelete = async (row: EditableRow) => {
        if (isFreePlan) return;
        if (missingShopUrl) return;
        if (allDeleting) return;
        if (!window.confirm("削除しますか？")) return;

        try {
            await apiDeleteJson("/api/item-costs", {
                shop_url: shopUrlSafe,
                year_month: ymDate,
                item_key: (row as any).old_item_key,
            });

            load();
        } catch (err: any) {
            pushToast("error", formatItemCostsWriteError("削除", err), 0);
        }
    };

    // -------------------------------------------
    // ★一括コピー開始（既存）
    // -------------------------------------------
    const startBulkCopy = async () => {
        if (isFreePlan) return;
        if (missingShopUrl) return;
        if (bulkRunning || allDeleting) return;

        const msgBase = isRange12
            ? `一括コピーを開始しますか？\n\n${bulkSummaryLabel}\n\n（テンプレ月の1ヶ月分を、コピー先12ヶ月に同じ値で複製します）`
            : `一括コピーを開始しますか？\n\n${bulkSummaryLabel}`;

        const msg =
            bulkMode === "overwrite"
                ? `${msgBase}\n\n※「上書き」は空欄（NULL）もコピーされます。\nコピー元が空欄の項目は、コピー先の値も消えます。`
                : `${msgBase}\n\n（大量データの場合、完了まで数分かかることがあります）`;

        if (!window.confirm(msg)) return;

        setBulkRunning(true);
        setBulkStatus(null);

        try {
            const resp = await apiPostJson("/api/item-costs/bulk-copy", {
                source_year_month: bulkSrcYm,
                dest_year_month: bulkDstYm,
                scope: bulkScope,
                mode: bulkMode,
                include_rates: bulkIncludeRates,
                include_items: true,
            });

            setBulkRunId(resp.runId);
        } catch (e: any) {
            pushToast("error", formatItemCostsWriteError("一括コピー開始", e), 0);
            setBulkRunning(false);
        }
    };

    const bulkFinalizedRef = useRef(false);
    const bulkInFlightRef = useRef(false);
    const bulkAlertedRunIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!bulkRunId) return;

        bulkFinalizedRef.current = false;

        let cancelled = false;
        let timer: any = null;

        const stop = () => {
            if (timer) {
                clearInterval(timer);
                timer = null;
            }
        };

        const finalizeOnce = async (kind: "done" | "error", st: any) => {
            if (bulkFinalizedRef.current) return;
            bulkFinalizedRef.current = true;
            stop();

            setBulkRunning(false);
            setBulkOpen(false);
            setBulkRunId(null);

            if (bulkAlertedRunIdRef.current === bulkRunId) return;
            bulkAlertedRunIdRef.current = bulkRunId;

            if (kind === "done") {
                pushToast("success", "一括コピーが完了しました");
                await load();
                return;
            }
            pushToast("error", formatItemCostsStatusError("一括コピー", st?.bq?.error?.message || "error"), 0);
        };

        const tick = async () => {
            if (cancelled) return;
            if (bulkFinalizedRef.current) return;
            if (bulkInFlightRef.current) return;

            bulkInFlightRef.current = true;
            try {
                const st = await apiGetJson(`/api/item-costs/bulk-copy/status?runId=${encodeURIComponent(bulkRunId)}`);
                if (cancelled) return;

                setBulkStatus(st);

                if (st.status === "done") {
                    await finalizeOnce("done", st);
                    return;
                }
                if (st.status === "error") {
                    await finalizeOnce("error", st);
                    return;
                }
            } catch {
            } finally {
                bulkInFlightRef.current = false;
            }
        };

        setBulkRunning(true);
        tick();
        timer = setInterval(tick, 2000);

        return () => {
            cancelled = true;
            stop();
        };
    }, [bulkRunId, load, pushToast]);

    // =========================================================
    // UI
    // =========================================================

    // ✅ overlay ローディング（白画面にならない）: FiscalRakutenExpensePage と同じ思想
    const overlayBusy = settingsLoading || (loading && rows.length === 0);

    // ✅ 年度候補（FiscalRakutenExpensePage と同じ）
    const baseYear = useMemo(() => computeDefaultYearForStartMonth(dayjs(), startMonth), [startMonth]);
    const yearOptions = useMemo(() => Array.from({ length: 6 }, (_, i) => baseYear - 2 + i), [baseYear]);

    return (
        <div className="relative space-y-2">
            {/* ✅ overlay ローディング */}
            {overlayBusy && (
                <div className="fixed top-14 bottom-0 left-0 md:left-[210px] right-0 z-50 flex items-center justify-center bg-white/0">
                    <LoadingInline />
                </div>
            )}

            {/* ✅ 一括削除：処理中オーバーレイ */}
            {bulkDeleting && (
                <div className="fixed inset-0 z-[2000] bg-black/20 flex items-center justify-center">
                    <div className="bg-white border rounded-lg shadow px-4 py-3 text-sm">一括削除中...</div>
                </div>
            )}

            {/* ✅ 全件削除：処理中オーバーレイ */}
            {allDeleting && (
                <div className="fixed inset-0 z-[2000] bg-black/20 flex items-center justify-center">
                    <div className="bg-white border rounded-lg shadow px-4 py-3 text-sm">全件削除中...</div>
                </div>
            )}

            {/* ✅ トースト（未保存ヒントは同じ枠の先頭に常駐させ、成功・失敗のお知らせと重ならないようにする） */}
            <ToastViewport toasts={toasts} onClose={removeToast}>
                {dirty && !dirtyHintDismissed && !saving && (
                    <div className="pointer-events-auto flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 shadow">
                        <div className="text-xs text-amber-900">変更があります。右上の「保存」を押してください。</div>
                        <button
                            className="ml-2 text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
                            onClick={() => setDirtyHintDismissed(true)}
                            aria-label="閉じる"
                        >
                            ×
                        </button>
                    </div>
                )}
            </ToastViewport>

            {bulkOpen && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-xl rounded-xl shadow-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="font-semibold text-sm">一括コピー</div>
                            <button
                                className="text-xs px-2 py-1 border rounded"
                                onClick={() => !bulkRunning && setBulkOpen(false)}
                                disabled={bulkRunning}
                            >
                                閉じる
                            </button>
                        </div>

                        <div className="text-xs text-slate-700 bg-slate-50 border rounded px-2 py-1">{bulkSummaryLabel}</div>

                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="space-y-1">
                                <div className="text-gray-600">コピー種別</div>
                                <select
                                    className="w-full border rounded px-2 py-1"
                                    value={bulkScope}
                                    onChange={(e) => setBulkScope(e.target.value as any)}
                                    disabled={bulkRunning}
                                >
                                    <option value="single">単月コピー（1ヶ月 → 1ヶ月）</option>
                                    <option value="range12">12ヶ月コピー（テンプレ月の内容を12ヶ月に複製）</option>
                                </select>
                            </div>

                            <div className="space-y-1">
                                <div className="text-gray-600">コピー方法</div>
                                <select
                                    className="w-full border rounded px-2 py-1"
                                    value={bulkMode}
                                    onChange={(e) => setBulkMode(e.target.value as any)}
                                    disabled={bulkRunning}
                                >
                                    <option value="insert_only">未登録のみ追加</option>
                                    <option value="fill">空欄だけ埋める</option>
                                    <option value="overwrite">上書き（空もコピーしてクリア）</option>
                                </select>
                            </div>

                            <div className="space-y-1">
                                <div className="text-gray-600">{isRange12 ? "コピー元基準月（テンプレ月）" : "コピー元年月"}</div>
                                <input
                                    type="month"
                                    className="w-full border rounded px-2 py-1"
                                    value={bulkSrcYm}
                                    onChange={(e) => setBulkSrcYm(e.target.value)}
                                    disabled={bulkRunning}
                                />
                                {isRange12 && (
                                    <div className="text-[11px] text-gray-500">
                                        ※この1ヶ月分の内容を、コピー先12ヶ月に複製します（テンプレ）
                                    </div>
                                )}
                            </div>

                            <div className="space-y-1">
                                <div className="text-gray-600">{isRange12 ? "コピー先開始月（ここから12ヶ月）" : "コピー先年月"}</div>
                                <input
                                    type="month"
                                    className="w-full border rounded px-2 py-1"
                                    value={bulkDstYm}
                                    onChange={(e) => setBulkDstYm(e.target.value)}
                                    disabled={bulkRunning}
                                />
                                <div className="text-[11px] text-gray-500">
                                    {isRange12 ? `コピー先範囲: ${bulkDstRangeLabel}` : `コピー先: ${bulkDstStartLabel}`}
                                </div>
                            </div>

                            <label className="col-span-2 flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={bulkIncludeRates}
                                    onChange={(e) => setBulkIncludeRates(e.target.checked)}
                                    disabled={bulkRunning}
                                />
                                <span>平均商品原価率もコピーする</span>
                            </label>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <div className="text-xs text-gray-600">
                                {bulkStatus?.runId ? (
                                    <>
                                        runId: <span className="font-mono">{bulkStatus.runId}</span> / status:{" "}
                                        <span className="font-semibold">{bulkStatus.status}</span>
                                    </>
                                ) : (
                                    "設定して開始してください"
                                )}
                            </div>

                            <button
                                className={`px-3 py-1 rounded text-xs text-white ${bulkRunning ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"}`}
                                onClick={startBulkCopy}
                                disabled={bulkRunning}
                            >
                                {bulkRunning ? "実行中..." : "一括コピー開始"}
                            </button>
                        </div>

                        {bulkStatus?.bq?.state && (
                            <div className="text-[11px] text-gray-600">
                                BigQuery: {bulkStatus.bq.state}
                                {bulkStatus?.dmlStats ? <> / dmlStats: {JSON.stringify(bulkStatus.dmlStats)}</> : null}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!isFreePlan && (
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: "none" }}
                    onChange={handleFileSelected}
                />
            )}

            <SupportAnchorBanner />

            {/* ネクストエンジン連携モーダル(コンパクト・a11y対応)。取得の経過/結果は
                下のバナー(原価一覧の画面側)に出す。 */}
            <NextEngineConnectModal
                open={neModalOpen}
                onClose={() => setNeModalOpen(false)}
                onJobStarted={(jobId) => {
                    nePolling.startPolling(jobId);
                    setNeModalOpen(false); // 取得を始めたらモーダルは閉じる
                }}
                onActiveJob={(jobId) => nePolling.startPolling(jobId)}
                jobRunning={neJobRunning}
            />

            {/* NE 原価取得の経過/結果/エラー(やさしい言葉・店舗別内訳・a11y) */}
            <NeSyncStatusBanner
                job={neJob}
                pollError={nePolling.error}
                onDismiss={() => {
                    nePolling.stop();
                    nePolling.setJob(null);
                }}
            />

            {/* ✅ shopUrl が無い場合も枠は出す（白画面回避） */}
            {missingShopUrl && <div className="mb-2 text-sm text-red-600">shopUrl が未指定です（このページは shopUrl が必要です）</div>}

                <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[15px] font-bold text-gray-900">登録状況（売上実績商品）</div>
                        <div className="mt-1 text-[11px] text-gray-500">
                            対象年月: {dayjs(coverageSummary.year_month).format("YYYY/MM")}
                        </div>
                    </div>
                    {!isFreePlan && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <label className="text-[11px] text-gray-600">チェック対象年月</label>
                            <input
                                type="month"
                                className="border px-2 py-1 rounded bg-white text-xs h-8"
                                value={coverageYearMonth}
                                onChange={(e) => setCoverageYearMonth(e.target.value)}
                                disabled={coverageLoading || missingShopUrl || allDeleting}
                            />
                        </div>
                    )}

                </div>

                {isFreePlan && (
                    <div className="mb-3 p-3 border rounded bg-amber-50 border-amber-200 text-amber-900 text-sm">
                        スタンダードプラン以上の方がご利用いただける機能です。商品管理番号、SKU管理番号ごとの原価を登録いただくことで、正確な原価計算、利益管理が実現します。
                    </div>
                )}

                <div className="mb-7 grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="mb-1 text-[11px] font-medium text-gray-400">売上実績商品の登録数</div>
                        <div className="leading-none">
                            <span className="text-[28px] font-semibold text-gray-900">
                                {isFreePlan ? "-" : coverageLoading ? "-" : coverageSummary.registered_items}
                            </span>
                            <span className="ml-1 text-[14px] font-medium text-gray-400">
                                / {isFreePlan ? "-" : coverageLoading ? "-" : coverageSummary.total_items}
                            </span>
                        </div>

                        <div className="mt-3 h-[4px] overflow-hidden rounded bg-gray-200">
                            <div
                                className="h-full rounded bg-blue-500"
                                style={{
                                    width: isFreePlan ? "0%" : `${Math.max(0, Math.min(100, coverageSummary.registration_rate || 0))}%`,
                                }}
                            />
                        </div>

                        <div className="mt-2 text-[11px] text-gray-500">
                            売上実績商品に対する登録率 {isFreePlan ? "-" : coverageLoading ? "-" : `${Math.round(coverageSummary.registration_rate)}%`}
                        </div>
                        <div className="mt-1 text-[10px] italic text-gray-400">
                            ※対象年月の売上実績商品にて計算
                        </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="mb-1 text-[11px] font-medium text-gray-400">未登録商品数</div>
                        <div className="leading-none text-[28px] font-semibold text-amber-600">
                            {isFreePlan ? "-" : coverageLoading ? "-" : coverageSummary.unregistered_items}
                        </div>

                        <div className="mt-1 text-[10px] italic text-gray-400">
                            ※対象年月の売上実績商品にて計算
                        </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="mb-1 text-[11px] font-medium text-gray-400">登録率</div>
                        <div className="leading-none text-[28px] font-semibold text-green-600">
                            {isFreePlan ? "-" : coverageLoading ? "-" : `${Math.round(coverageSummary.registration_rate)}%`}
                        </div>

                        <div className="mt-3 text-[11px] text-gray-500">
                            一致している割合
                        </div>
                        <div className="mt-1 text-[10px] italic text-gray-400">
                            ※対象年月の売上実績商品にて計算
                        </div>
                    </div>
                </div>

            {!isFreePlan && coverageSummary.unregistered_details.length > 0 && (
                <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="mb-2 text-sm font-semibold text-amber-800">
                        未登録商品一覧
                        </div>
                        <button
                            type="button"
                            onClick={downloadUnregisteredCsv}
                            className="px-2 py-1 rounded border border-amber-300 bg-white hover:bg-amber-50 text-xs text-amber-800"
                            title="未登録商品の商品管理番号・SKU番号が入った状態のCSVをダウンロードします。原価を入力して一括アップロードに使えます。"
                        >
                            CSVダウンロード（インポート用）
                        </button>
                    </div>
                    <div className="mb-3 text-xs text-amber-700">
                        未登録商品一覧には、当月に売上があった原価未登録の商品が表示されます。そのため、表示される商品は毎月の売上状況に応じて変動いたします。
                        <br />
                        個別原価は未登録です。平均商品原価率が設定されている場合は、その値で利益計算されます。
                    </div>

                    <div className="overflow-x-auto rounded-lg border border-amber-200 bg-white">
                        <table className="min-w-full text-sm">
                            <thead className="bg-amber-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-amber-800">商品管理番号</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-amber-800">SKU管理番号</th>
                                </tr>
                            </thead>
                            <tbody>
                                {coverageSummary.unregistered_details.map((item) => (
                                    <tr key={item.item_key} className="border-t border-amber-100">
                                        <td className="px-3 py-2 text-xs text-gray-700">{item.item_code}</td>
                                        <td className="px-3 py-2 text-xs text-gray-700">{item.sku_code}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-2 text-[10px] text-amber-700">
                        ※表示上限は100件です
                    </div>
                </div>
            )}



            <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold">商品原価（年度 / 12ヶ月）</div>

                    <label className="text-xs text-gray-600 ml-2">年度</label>
                    <select
                        className="border px-2 py-1 rounded bg-white text-sm h-8"
                        value={year}
                        onChange={(e) => {
                            if (!confirmDiscardIfDirty()) return;
                            userChangedYearRef.current = true;
                            setYear(Number(e.target.value));
                            setPage(1);
                        }}
                        disabled={settingsLoading}
                    >
                        {yearOptions.map((y) => (
                            <option key={y} value={y}>
                                {y}年度
                            </option>
                        ))}
                    </select>

                    <div className="text-sm font-semibold w-44 text-center">{yearRangeLabelFromYearStartMonth(year, startMonth)}</div>

                    <button
                        className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            userChangedYearRef.current = true;
                            setYear((prev) => prev - 1);
                            setPage(1);
                        }}
                        disabled={settingsLoading}
                        title="前年（年度）へ"
                    >
                        ← 前年
                    </button>

                    <button
                        className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            userChangedYearRef.current = true;
                            setYear((prev) => prev + 1);
                            setPage(1);
                        }}
                        disabled={settingsLoading}
                        title="翌年（年度）へ"
                    >
                        翌年 →
                    </button>

                    <div className="text-[11px] text-gray-600 ml-2">期首月: {startMonth}月 / year_month: {ymDate}</div>
                </div>

                <div className="flex flex-col items-end gap-2">
                    {/* 1段目 */}
                    <div className="flex gap-2 flex-wrap justify-end">
                        <button type="button" onClick={downloadSampleXlsx} className="bg-slate-700 text-white px-3 py-1 rounded text-xs">
                            原価登録サンプル（Excel）をダウンロード
                        </button>

                        <button
                            type="button"
                            onClick={downloadAllCsv}
                            className="bg-slate-700 text-white px-3 py-1 rounded text-xs"
                            disabled={missingShopUrl || allDeleting}
                        >
                            登録済原価全件ダウンロード
                        </button>

                        <button
                            type="button"
                            onClick={downloadTemplateCsv}
                            className="bg-slate-700 text-white px-3 py-1 rounded text-xs"
                            disabled={missingShopUrl || allDeleting}
                        >
                            サンプルフォーマットをダウンロード
                        </button>

                        {!isFreePlan && (
                            <button
                                className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                                disabled={importing || missingShopUrl || allDeleting}
                                onClick={handleClickImport}
                                title="CSVを選択して当期（12ヶ月表）に未登録商品だけ追加します（既存は無視）"
                            >
                                {importing ? "取込中..." : "一括アップロード"}
                            </button>
                        )}

                        {isSuper && (
                            <button
                                className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                                disabled={getting || importing || missingShopUrl || allDeleting}
                                onClick={handleClickGetRms}
                                title="RMSから商品/SKUを取得"
                            >
                                {getting ? "取得中..." : "RMS取得"}
                            </button>
                        )}

                        {!isFreePlan && (
                            <button
                                className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                                disabled={missingShopUrl || allDeleting}
                                onClick={() => setNeModalOpen(true)}
                                title="ネクストエンジンから商品原価を取得・連携します"
                            >
                                ネクストエンジン連携
                            </button>
                        )}
                    </div>

                    {/* 2段目：一括コピー / 一括削除 / 全件削除 / 保存 */}
                    <div className="flex gap-2 flex-wrap justify-end items-center">
                        {!isFreePlan && (
                            <button
                                className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                                onClick={() => {
                                    setBulkSrcYm(dayjs().subtract(1, "month").format("YYYY-MM"));
                                    setBulkDstYm(yearMonth);

                                    setBulkScope("single");
                                    setBulkMode("insert_only");
                                    setBulkIncludeRates(true);
                                    setBulkStatus(null);
                                    setBulkRunId(null);
                                    setBulkRunning(false);
                                    prevScopeRef.current = "single";
                                    setBulkOpen(true);
                                }}
                                title="大量でもサーバ側で高速コピーします"
                                disabled={bulkDeleting || allDeleting || missingShopUrl}
                            >
                                一括コピー
                            </button>
                        )}

                        {!isFreePlan && (
                            <>
                                <button
                                    className={`px-3 py-1 rounded text-xs text-white ${selectedCount === 0 || bulkDeleting || allDeleting || missingShopUrl
                                        ? "bg-gray-400"
                                        : "bg-red-600 hover:bg-red-700"
                                        }`}
                                    disabled={selectedCount === 0 || bulkDeleting || allDeleting || missingShopUrl}
                                    onClick={handleBulkDeleteSelected}
                                    title="チェックした行をまとめて削除します"
                                >
                                    {bulkDeleting
                                        ? `一括削除中...（${selectedCount}件）`
                                        : `一括削除${selectedCount > 0 ? `（${selectedCount}件）` : ""}`}
                                </button>

                                <button
                                    className={`px-3 py-1 rounded text-xs text-white ${bulkDeleting || allDeleting || saving || missingShopUrl ? "bg-gray-400" : "bg-red-800 hover:bg-red-900"
                                        }`}
                                    disabled={bulkDeleting || allDeleting || saving || missingShopUrl}
                                    onClick={handleDeleteAll}
                                    title="この店舗に紐づく商品原価データを全件削除します"
                                >
                                    {allDeleting ? "全件削除中..." : "全件削除"}
                                </button>

                                <div className="text-[11px] text-gray-600">
                                    選択: {selectedCount} / {selectableCount}
                                    {bulkDeleting ? <span className="ml-2 text-red-600">削除中...</span> : null}
                                    {allDeleting ? <span className="ml-2 text-red-700">全件削除中...</span> : null}
                                </div>
                            </>
                        )}

                        <button
                            className={`px-3 py-1 bg-blue-600 text-white rounded text-xs ${saving ? "opacity-60 cursor-not-allowed" : ""} ${dirty ? "ring-2 ring-amber-400" : ""
                                }`}
                            disabled={saving || bulkDeleting || allDeleting || missingShopUrl}
                            onClick={handleSave}
                        >
                            {saving ? "保存中..." : "保存"}
                        </button>
                    </div>
                </div>
            </div>

            {isFreePlan && (
                <div className="p-3 border rounded bg-amber-50 border-amber-200 text-amber-900 text-sm">
                    フリープランでは <b>平均商品原価率のみ</b> 利用できます。
                    <br />
                    商品原価（SKU/商品別）の登録・変更・削除、およびCSVインポートは利用できません。
                </div>
            )}

            {!isFreePlan && (
                <div className="w-full flex gap-1 items-center">
                    <input
                        type="text"
                        placeholder="商品管理番号 / SKU管理番号 / 商品名で検索"
                        className="border px-2 py-1 rounded w-80 text-xs"
                        value={keyword}
                        onChange={handleKeywordChange}
                        disabled={missingShopUrl || allDeleting}
                    />
                    <button onClick={() => load()} disabled={missingShopUrl || allDeleting} className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-xs">
                        検索
                    </button>
                </div>
            )}

            {!isFreePlan && (
                <div className="flex items-center gap-2 text-xs">
                    <button
                        disabled={page <= 1 || missingShopUrl || allDeleting}
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setPage(1);
                        }}
                        className="border rounded px-2 py-0.5 disabled:opacity-40"
                    >
                        ≪
                    </button>
                    <button
                        disabled={page <= 1 || missingShopUrl || allDeleting}
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setPage(page - 1);
                        }}
                        className="border rounded px-2 py-0.5 disabled:opacity-40"
                    >
                        前へ
                    </button>
                    <span>
                        {page} / {Math.max(1, Math.ceil(total / pageSize))}
                    </span>
                    <button
                        disabled={page >= Math.ceil(total / pageSize) || missingShopUrl || allDeleting}
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setPage(page + 1);
                        }}
                        className="border rounded px-2 py-0.5 disabled:opacity-40"
                    >
                        次へ
                    </button>
                    <button
                        disabled={page >= Math.ceil(total / pageSize) || missingShopUrl || allDeleting}
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setPage(Math.ceil(total / pageSize));
                        }}
                        className="border rounded px-2 py-0.5 disabled:opacity-40"
                    >
                        ≫
                    </button>
                </div>
            )}

            {(settingsLoading || loading) && rows.length === 0 ? (
                <LoadingInline />
            ) : (
                <>
                    <MonthlyItemCostTable
                        rows={isFreePlan ? [] : rows}
                        months={(tableMonthCols.length === 12 ? tableMonthCols : monthColsSafe) as any}
                        onChange={handleChange}
                        onDelete={handleDelete}
                        avgCostRatesView={avgCostRatesView}
                        onChangeAvgCostRate={handleAvgRateChange}
                        hideItemRows={isFreePlan}
                        showItemName
                        selectable={!isFreePlan}
                        selectedKeys={selectedKeys}
                        onToggleSelect={onToggleSelect}
                        onToggleSelectAll={onToggleSelectAll}
                    />
                </>
            )}

            <CostSupportInfo />
        </div>
    );
}